import { Bank, ArrowSquareOut, Star, ShieldCheck, Coins } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

const HYSA_PLATFORMS = [
  {
    rank: 1,
    name: "Newtek Bank",
    apy: "4.35%",
    min: "$0",
    fees: "None",
    fdic: true,
    highlight: "Highest APY available — no minimums or fees",
    url: "https://www.newtekbank.com/personal-high-yield-savings/",
  },
  {
    rank: 2,
    name: "Axos Bank",
    apy: "4.21%",
    min: "$0",
    fees: "None",
    fdic: true,
    highlight: "No minimums, no fees, consistently top-tier rate",
    url: "https://www.axosbank.com/personal/savings",
  },
  {
    rank: 3,
    name: "Varo Bank",
    apy: "5.00%",
    min: "$0",
    fees: "None",
    fdic: true,
    highlight: "5% APY on first $5,000 with qualifying direct deposits",
    note: "Requires direct deposit. 3% on balances above $5K",
    url: "https://www.varomoney.com/savings-account/",
  },
  {
    rank: 4,
    name: "Vio Bank",
    apy: "4.03%",
    min: "$100",
    fees: "None",
    fdic: true,
    highlight: "Low $100 minimum, competitive yield",
    url: "https://www.viobank.com/high-yield-online-savings-account",
  },
  {
    rank: 5,
    name: "LendingClub",
    apy: "4.00%",
    min: "$0",
    fees: "None",
    fdic: true,
    highlight: "Higher yield with $250+ monthly deposits. Pairs well with their checking account",
    url: "https://www.lendingclub.com/personal-savings/high-yield-savings",
  },
  {
    rank: 6,
    name: "Bread Savings",
    apy: "4.00%",
    min: "$100",
    fees: "None",
    fdic: true,
    highlight: "Straightforward, no-frills high yield savings",
    url: "https://www.breadfinancial.com/en/bread-savings.html",
  },
  {
    rank: 7,
    name: "SoFi",
    apy: "3.80%",
    min: "$0",
    fees: "None",
    fdic: true,
    highlight: "Up to 4.00% with direct deposit. Full banking ecosystem with vaults and checking",
    url: "https://www.sofi.com/banking/savings-account/",
  },
  {
    rank: 8,
    name: "CIT Bank Platinum",
    apy: "3.90%",
    min: "$0",
    fees: "None",
    fdic: true,
    highlight: "Up to $300 bonus for new accounts. Multiple savings tiers",
    url: "https://www.cit.com/cit-bank/bank/savings/savings-connect/",
  },
  {
    rank: 9,
    name: "EverBank",
    apy: "3.90%",
    min: "$0",
    fees: "None",
    fdic: true,
    highlight: "No fees, also offers competitive CDs and checking",
    url: "https://www.everbank.com/banking/online-savings-account",
  },
  {
    rank: 10,
    name: "Openbank",
    apy: "4.00%",
    min: "$500",
    fees: "None",
    fdic: true,
    highlight: "Backed by Santander. Strong 4.00% rate",
    url: "https://www.openbank.com/savings-account",
  },
];

export default function HYSA() {
  return (
    <div data-testid="hysa-page">
      <div className="bg-zinc-950 border-b border-zinc-800/60 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <Bank size={22} weight="bold" className="text-emerald-400" />
          <h1 className="font-heading text-2xl font-bold text-zinc-50 tracking-tight">High Yield Savings</h1>
          <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-emerald-900/30 text-emerald-400 h-4">HYSA</Badge>
        </div>
        <p className="text-xs text-zinc-500 mt-1">Top platforms offering the best savings rates — all FDIC insured. Rates are variable and updated periodically.</p>
      </div>

      <div className="p-5 max-w-[1000px] space-y-5">

        {/* Quick context */}
        <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-4 space-y-2">
          <h2 className="text-sm font-heading font-bold text-zinc-200 flex items-center gap-2">
            <ShieldCheck size={14} weight="bold" className="text-emerald-400" />
            Why a High Yield Savings Account?
          </h2>
          <ul className="space-y-1.5">
            {[
              "National average savings rate is ~0.6%. These accounts offer 4-5% — that's 6-8x more on your money",
              "FDIC insured up to $250,000 per depositor — your money is protected by the US government",
              "Completely liquid — withdraw anytime with no penalties, unlike CDs",
              "Best for: emergency funds, short-term savings, cash reserves between investments",
              "Interest compounds daily or monthly, growing your balance automatically",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13px] text-zinc-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Quick math */}
        <div className="bg-emerald-950/15 border border-emerald-800/20 rounded-sm px-4 py-3">
          <p className="text-xs text-emerald-300 leading-relaxed">
            <strong>Quick math:</strong> $10,000 at 4.35% APY earns ~$435/year in interest — compared to ~$60/year at a traditional bank. On $50,000 that's $2,175 vs $300. Your idle cash should be working for you.
          </p>
        </div>

        {/* Platform cards */}
        <div className="space-y-2.5">
          {HYSA_PLATFORMS.map((p) => (
            <a
              key={p.rank}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`hysa-platform-${p.rank}`}
              className="group block bg-zinc-900/50 border border-zinc-800/60 rounded-sm hover:border-emerald-800/40 transition-colors"
            >
              <div className="px-4 py-3.5">
                <div className="flex items-start gap-4">
                  {/* Rank */}
                  <div className="w-8 h-8 rounded-sm bg-zinc-800/60 flex items-center justify-center shrink-0">
                    <span className={`text-sm font-data font-bold ${p.rank <= 3 ? "text-emerald-400" : "text-zinc-400"}`}>
                      {p.rank}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-heading font-bold text-zinc-100 group-hover:text-emerald-400 transition-colors">{p.name}</span>
                      <ArrowSquareOut size={12} className="text-zinc-700 group-hover:text-emerald-500 transition-colors" />
                      {p.fdic && (
                        <Badge className="text-[7px] px-1 py-0 border-0 h-3.5 bg-emerald-900/30 text-emerald-400">FDIC</Badge>
                      )}
                      {p.rank === 3 && (
                        <Badge className="text-[7px] px-1 py-0 border-0 h-3.5 bg-amber-900/30 text-amber-400">CONDITIONAL</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-0.5">{p.highlight}</p>
                    {p.note && <p className="text-[10px] text-amber-500/70 mt-0.5">{p.note}</p>}
                  </div>

                  {/* APY + Details */}
                  <div className="text-right shrink-0">
                    <p className={`text-lg font-data font-bold ${p.rank <= 3 ? "text-emerald-400" : "text-zinc-200"}`}>{p.apy}</p>
                    <p className="text-[9px] text-zinc-600">APY</p>
                  </div>
                </div>

                {/* Bottom row */}
                <div className="flex items-center gap-4 mt-2 ml-12 text-[10px] text-zinc-600">
                  <span>Min: <strong className="text-zinc-400">{p.min}</strong></span>
                  <span>Fees: <strong className="text-zinc-400">{p.fees}</strong></span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* CEX Earn Section */}
        <div className="pt-4 border-t border-zinc-800/40">
          <div className="flex items-center gap-2.5 mb-4">
            <Coins size={18} weight="bold" className="text-amber-400" />
            <h2 className="text-lg font-heading font-bold text-zinc-100">Top 10 Crypto Exchanges — Best Earn Rates</h2>
            <Badge className="text-[8px] px-1.5 py-0.5 border-0 bg-amber-900/30 text-amber-400 h-4">CRYPTO</Badge>
          </div>

          <div className="bg-amber-950/10 border border-amber-800/15 rounded-sm px-4 py-2.5 mb-4">
            <p className="text-xs text-amber-300/80 leading-relaxed">
              <strong>Note:</strong> Unlike FDIC-insured bank accounts, crypto earn/savings products carry platform risk. Rates shown are for stablecoins (USDC/USDT) unless noted. Higher rates may require token staking or lock-up periods.
            </p>
          </div>

          <div className="space-y-2.5">
            {[
              {
                rank: 1,
                name: "Nexo",
                rate: "Up to 14%",
                rateDetail: "Flex: 8-13% | Fixed: up to 14%",
                assets: "BTC 5.7% | ETH 6.25% | USDT 11.5%",
                highlight: "Highest stablecoin rates. Daily compounding. Flexible or fixed terms available",
                note: "Platinum tier (10%+ NEXO holdings) required for max rates. Earn in NEXO tokens for +2% boost",
                url: "https://nexo.com/earn-crypto",
              },
              {
                rank: 2,
                name: "Crypto.com",
                rate: "Up to 5%",
                rateDetail: "Standard: 1.5% | Pro: 4.5% | Private: 5%",
                assets: "USDC 3.7% | USDT 3.7% | BTC ~2%",
                highlight: "Tiered rates based on membership. Pro membership ($30/mo) unlocks best rates",
                note: "Interest paid monthly in CRO. Higher rates with CRO staking + lock-up",
                url: "https://crypto.com/us/earn",
              },
              {
                rank: 3,
                name: "Gemini",
                rate: "Up to 7.9%",
                rateDetail: "GUSD: 7.9% | ETH: 3% | SOL: 6%",
                assets: "GUSD 7.9% | ETH 3% (staking) | SOL 6% (staking)",
                highlight: "Best GUSD (Gemini Dollar) yield at ~7.9%. Daily payouts. No trading fees for GUSD",
                note: "ETH/SOL are staking rewards (30% fee deducted). Stablecoins via lending",
                url: "https://www.gemini.com/earn",
              },
              {
                rank: 4,
                name: "Binance",
                rate: "Up to 6.5%",
                rateDetail: "Simple Earn Flex: ~3-4% | Locked: up to 6.5%",
                assets: "USDT 3.2% flex | BNB 1.3% | BTC 0.5%",
                highlight: "Largest exchange. Simple Earn products with flexible and locked options",
                note: "Best rates on locked products (30-120 day terms). BNB holdings can boost rates",
                url: "https://www.binance.com/en/earn",
              },
              {
                rank: 5,
                name: "OKX",
                rate: "Up to 10%",
                rateDetail: "Simple Earn: 3-5% | Structured: up to 10%",
                assets: "USDT 3.5% flex | ETH 3.2% | BTC 1.5%",
                highlight: "Competitive flexible earn rates. Structured products offer higher yields with conditions",
                note: "Structured products involve risk. Simple Earn is lower risk with flexible withdrawal",
                url: "https://www.okx.com/earn",
              },
              {
                rank: 6,
                name: "Kraken",
                rate: "Up to 5%",
                rateDetail: "USDC: 5% bonded | 4% flex | BTC: 0.1%",
                assets: "USDC 5% | USDT 4% | DOT 8% | SOL 4%",
                highlight: "Trusted US exchange. Opt-in staking rewards with flexible or bonded terms",
                note: "Bonded (30-day lock) required for top rates. Strong regulatory compliance",
                url: "https://www.kraken.com/features/earn-crypto",
              },
              {
                rank: 7,
                name: "Bybit",
                rate: "Up to 15%",
                rateDetail: "Flex: 2-11% | Fixed: up to 15% promo",
                assets: "USDT 4-12% tiered | BTC ~2.5% | USDe 3.5%",
                highlight: "Tiered rates — higher APR on smaller balances. Auto-Earn for idle funds. Frequent promos",
                note: "Top rates are tiered (e.g., 12% on first 500 USDT only). Promo rates are short-term",
                url: "https://www.bybit.com/en/earn/home/",
              },
              {
                rank: 8,
                name: "KuCoin",
                rate: "Up to 30%",
                rateDetail: "P2P Lending: market-driven | Earn: 3-8%",
                assets: "USDT 5-30% lending | BTC market-driven | KCS 3%",
                highlight: "Peer-to-peer lending with hourly rate matching. Rates surge during bull markets",
                note: "Lending rates are highly variable — 30% only during peak demand. You set minimum APY. 85% payout after insurance fee",
                url: "https://www.kucoin.com/earn",
              },
              {
                rank: 9,
                name: "YouHodler",
                rate: "Up to 12%",
                rateDetail: "Stablecoins: 10-12% | BTC: 3% | ETH: 5.5%",
                assets: "USDT 12% | USDC 12% | BTC 3% | ETH 5.5%",
                highlight: "Swiss-based platform with consistently high stablecoin yields. Weekly payouts",
                note: "Not available in the US. Funds are used in lending/trading strategies. EU/Swiss regulated",
                url: "https://www.youhodler.com/earn",
              },
              {
                rank: 10,
                name: "Bitget",
                rate: "Up to 8%",
                rateDetail: "Savings: 2-5% flex | Staking: up to 8%",
                assets: "USDT 3.5% flex | BTC 1.2% | ETH 2.5%",
                highlight: "Growing exchange with competitive earn products. Copy-trading integration",
                note: "Lock-up periods for higher rates. Growing platform — verify current availability in your region",
                url: "https://www.bitget.com/earn",
              },
            ].map((p) => (
              <a
                key={p.rank}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`cex-earn-${p.rank}`}
                className="group block bg-zinc-900/50 border border-zinc-800/60 rounded-sm hover:border-amber-800/40 transition-colors"
              >
                <div className="px-4 py-3.5">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-sm bg-zinc-800/60 flex items-center justify-center shrink-0">
                      <span className={`text-sm font-data font-bold ${p.rank <= 2 ? "text-amber-400" : "text-zinc-400"}`}>{p.rank}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-heading font-bold text-zinc-100 group-hover:text-amber-400 transition-colors">{p.name}</span>
                        <ArrowSquareOut size={12} className="text-zinc-700 group-hover:text-amber-500 transition-colors" />
                        <Badge className="text-[7px] px-1 py-0 border-0 h-3.5 bg-amber-900/30 text-amber-400">CEX</Badge>
                      </div>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{p.highlight}</p>
                      {p.note && <p className="text-[10px] text-amber-500/60 mt-0.5">{p.note}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-lg font-data font-bold ${p.rank <= 2 ? "text-amber-400" : "text-zinc-200"}`}>{p.rate}</p>
                      <p className="text-[9px] text-zinc-600">APY</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 ml-12 text-[10px] text-zinc-600 flex-wrap">
                    <span className="text-zinc-500">{p.rateDetail}</span>
                  </div>
                  <div className="mt-1 ml-12 text-[10px] text-zinc-600">
                    <span className="text-zinc-500">Top assets: </span>
                    <span className="text-zinc-400">{p.assets}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-zinc-900/30 border border-zinc-800/40 rounded-sm px-4 py-2.5">
          <p className="text-[10px] text-zinc-600 leading-relaxed">
            <strong>Disclaimer:</strong> Bank HYSA rates are variable and subject to change based on Federal Reserve policy. All listed banks are FDIC insured up to $250,000. Crypto exchange earn products are NOT FDIC insured and carry platform/counterparty risk. Rates shown were accurate as of early 2025 — always verify current rates directly with each institution. This is informational only, not financial advice.
          </p>
        </div>
      </div>
    </div>
  );
}
